"use client";

import React from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import VideoGalleryCard, { type VideoGalleryCardProps } from "@/components/VideoGalleryCard";
import { toAbsoluteMediaUrl } from "@/lib/media";
import { toCdn, feedThumb } from "@/lib/imageUrls";

export type VideoFeedItem = {
  type: "video";
  id: number | string;
  title?: string | null;
  thumbnailUrl?: string | null;
  durationSec?: number | null;
  viewCount?: number | null;
};

export type ImageFeedItem = {
  type: "image" | "gallery";
  id: number | string;
  src?: string | null; // can be relative key
  // What /api/feed ACTUALLY sends for image items (FeedItemDto): a
  // thumbnailUrl (often empty) and an images[] array of absolute URLs. The
  // original `src`-only shape matched nothing the API produces, so every
  // image card rendered as an empty box (2026-07-24).
  thumbnailUrl?: string | null;
  images?: (string | null)[] | null;
  title?: string | null;
  description?: string | null;
};

export type PostFeedItem = {
  type: "post";
  id: number | string;
  title?: string | null;
  snippet?: string | null;
  thumbnailUrl?: string | null;
  category?: string | null;
};

export type FeedItem = VideoFeedItem | ImageFeedItem | PostFeedItem | { type: string; [k: string]: any };

export function FeedCard({ item, onOpenImage }: { item: FeedItem; onOpenImage?: (src: string) => void }) {
  if (!item || typeof item !== "object" || typeof (item as any).type !== "string") return null;

  if (item.type === "video") {
    const v = item as VideoFeedItem;
    const props: VideoGalleryCardProps = {
      id: Number(v.id),
      title: String(v.title ?? ""),
      thumbnailUrl: String(v.thumbnailUrl || ""),
      viewCount: typeof v.viewCount === "number" ? v.viewCount : null,
    };
    return <VideoGalleryCard {...props} />;
  }

  if (item.type === "image" || item.type === "gallery") {
    const i = item as ImageFeedItem;
    // Fall back through every field the API can carry the image in.
    const rawSource = i.src || i.thumbnailUrl || (Array.isArray(i.images) ? i.images.find(Boolean) : null) || '';
    // Prefer CDN thumbnail for cards
    const src = (() => {
      const raw = String(rawSource).trim();
      if (!raw) return '';
      const derive = (path: string) => {
        const cleaned = path.replace(/^\/+/, '');
        // The feed's images[] URLs are ALREADY the _thumb variant; running
        // feedThumb on them would mint a nonexistent _thumb_thumb key.
        const keyed = /_thumb\.[a-z0-9]+$/i.test(cleaned) ? cleaned : feedThumb(cleaned).replace(/^\/+/, '');
        return toCdn(keyed, 'feed');
      };
      try { const u = new URL(raw); return derive(u.pathname); }
      catch { return derive(raw); }
    })();
    const full = toAbsoluteMediaUrl(String(rawSource) || undefined);
    const prefetchFull = () => { if (full) { const im = new Image(); im.decoding = 'async'; im.src = full; } };
    return (
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => { if (full) onOpenImage?.(full); }}
          className="group block w-full text-left"
          aria-label={i.title ? `Open image: ${i.title}` : "Open image"}
          onMouseEnter={prefetchFull}
          onFocus={prefetchFull}
        >
          <div className="relative aspect-square bg-muted">
            {src ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={src} alt={i.title || 'Image'} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" width={256} height={256} />
            ) : null}
          </div>
          {i.title && (
            <CardContent className="p-3">
              <div className="text-sm font-medium line-clamp-2">{i.title}</div>
              {i.description && (
                <div className="text-xs text-muted-foreground line-clamp-2">{i.description}</div>
              )}
            </CardContent>
          )}
        </button>
      </Card>
    );
  }

  if (item.type === "post") {
    const p = item as PostFeedItem;
    return (
      <Link href={`/posts/${encodeURIComponent(String(p.id))}`} className="block">
        <Card className="overflow-hidden flex flex-col h-full">
          {p.category && (
            <div className="px-3 py-2 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {p.category}
            </div>
          )}
          {p.thumbnailUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={((): string => { const raw = String(p.thumbnailUrl).trim(); try { const u = new URL(raw); const path = u.pathname.replace(/^\/+/, ''); return toCdn(feedThumb(path).replace(/^\/+/, ''), 'feed'); } catch { const cleaned = raw.replace(/^\/+/, ''); return toCdn(feedThumb(cleaned).replace(/^\/+/, ''), 'feed'); } })()}
              alt={p.title || 'Post thumbnail'}
              className="w-full aspect-video object-cover"
              loading="lazy"
              decoding="async"
              width={400}
              height={225}
            />
          ) : null}
          <CardContent className="p-4 flex flex-col gap-2 flex-grow">
            <div className="text-sm font-semibold line-clamp-2">{p.title || "Post"}</div>
            {p.snippet && <div className="text-xs text-muted-foreground line-clamp-3">{p.snippet}</div>}
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Fallback: unknown item, ignore gracefully
  return null;
}

export default FeedCard;
